/**
 * HMAC-SHA256 request signing (must match backend requestSigningService canonical format).
 */

const SIGNED_ENDPOINT_PREFIXES = [
  '/auth/register',
  '/auth/login',
  '/auth/validate-credentials',
  '/auth/refresh-token',
  '/auth/phone/login',
  '/auth/login/phone',
  '/auth/email/login',
  '/auth/login/email',
  '/payment/deposit',
  '/payment/deposit-multi',
  '/payment/withdrawal',
  '/payment/withdrawal/otp/send',
  '/payment/withdrawal/otp/verify',
  '/kiosk/heartbeat',
  '/users/me/redeem-requests',
] as const;

const MUTATING_METHODS = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

function getSigningSecret(): string | null {
  const secret = import.meta.env.VITE_REQUEST_SIGNING_SECRET;
  if (!secret || typeof secret !== 'string' || secret.length < 16) {
    return null;
  }
  return secret;
}

export function isRequestSigningConfigured(): boolean {
  return getSigningSecret() != null;
}

export function requiresRequestSignature(method: string, endpoint: string): boolean {
  const m = method.toUpperCase();
  if (!MUTATING_METHODS.has(m)) {
    return false;
  }
  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return SIGNED_ENDPOINT_PREFIXES.some(
    (prefix) =>
      path === prefix ||
      path.startsWith(`${prefix}?`) ||
      path.startsWith(`${prefix}/`)
  );
}

function normalizeSigningPath(apiPath: string): string {
  const pathOnly = apiPath.split('?')[0] || '/';
  if (pathOnly.length > 1 && pathOnly.endsWith('/')) {
    return pathOnly.slice(0, -1);
  }
  return pathOnly;
}

function buildCanonicalString(
  method: string,
  apiPath: string,
  timestampMs: number,
  rawBody: string
): string {
  const m = method.toUpperCase();
  const p = normalizeSigningPath(apiPath);
  return `${m}\n${p}\n${timestampMs}\n${rawBody}`;
}

async function hmacSha256Hex(secret: string, message: string): Promise<string> {
  const enc = new TextEncoder();
  const key = await crypto.subtle.importKey(
    'raw',
    enc.encode(secret),
    { name: 'HMAC', hash: 'SHA-256' },
    false,
    ['sign']
  );
  const sig = await crypto.subtle.sign('HMAC', key, enc.encode(message));
  return Array.from(new Uint8Array(sig))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

/**
 * @param endpoint - route without /api prefix, e.g. /auth/register
 * @param body - request JSON body or undefined for empty body
 */
export async function buildRequestSigningHeaders(
  method: string,
  endpoint: string,
  body?: unknown
): Promise<Record<string, string>> {
  const secret = getSigningSecret();
  if (!secret || !requiresRequestSignature(method, endpoint)) {
    return {};
  }

  const timestampMs = Date.now();
  const rawBody =
    body !== undefined && body !== null ? JSON.stringify(body) : '';
  const apiPath = `/api${endpoint.startsWith('/') ? endpoint : `/${endpoint}`}`;
  const canonical = buildCanonicalString(method, apiPath, timestampMs, rawBody);
  const signature = await hmacSha256Hex(secret, canonical);

  return {
    'X-Timestamp': String(timestampMs),
    'X-Signature': signature,
  };
}
